import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Truck, Save, Package, User, MapPin, CreditCard, Clock, ChevronRight, Printer, FileText, Eye, Search, X } from "lucide-react";

const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p);
const formatDate = (d) => d ? new Date(d).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";

const payColors = { pending: "bg-yellow-900/30 text-yellow-400 border-yellow-800/40", paid: "bg-green-900/30 text-green-400 border-green-800/40", failed: "bg-red-900/30 text-red-400 border-red-800/40", expired: "bg-gray-800/30 text-gray-500 border-gray-700/40" };
const orderColors = { pending: "bg-yellow-900/30 text-yellow-400 border-yellow-800/40", processing: "bg-blue-900/30 text-blue-400 border-blue-800/40", shipped: "bg-purple-900/30 text-purple-400 border-purple-800/40", delivered: "bg-green-900/30 text-green-400 border-green-800/40", cancelled: "bg-red-900/30 text-red-400 border-red-800/40", expired: "bg-gray-800/30 text-gray-500 border-gray-700/40" };

const payLabels = { pending: "Menunggu", paid: "Lunas", failed: "Gagal", expired: "Kedaluwarsa" };
const orderLabels = { pending: "Pending", processing: "Diproses", shipped: "Dikirim", delivered: "Selesai", cancelled: "Dibatalkan", expired: "Kedaluwarsa" };

const STATUS_ORDER = { pending: 0, processing: 1, shipped: 2, delivered: 3 };

const courierOptions = [
  { value: "jne", label: "JNE" },
  { value: "jnt", label: "J&T Express" },
  { value: "sicepat", label: "SiCepat" },
  { value: "tiki", label: "TIKI" },
  { value: "pos", label: "POS Indonesia" },
  { value: "anteraja", label: "AnterAja" },
];

function getNextStatuses(current) {
  const rank = STATUS_ORDER[current] ?? -1;
  return Object.entries(STATUS_ORDER)
    .filter(([, v]) => v > rank)
    .map(([k]) => k);
}

function OrderDetailModal({ order, open, onClose, shopInfo }) {
  const [tracking, setTracking] = useState(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const labelRef = useRef(null);

  useEffect(() => {
    if (open && order?.awb_number) {
      setTrackingLoading(true);
      api.get(`/orders/${order.id}/tracking`)
        .then(({ data }) => setTracking(data))
        .catch(() => setTracking(null))
        .finally(() => setTrackingLoading(false));
    } else {
      setTracking(null);
    }
  }, [open, order]);

  const printLabel = () => {
    const w = window.open("", "_blank", "width=420,height=600");
    if (!w) { toast.error("Popup diblokir browser"); return; }
    const senderName = shopInfo?.shop_name || "FI Barbershop Sekip";
    const senderAddr = shopInfo?.shop_address || "-";
    const senderPhone = shopInfo?.shop_phone || "-";
    w.document.write(`<!DOCTYPE html><html><head><title>Label Pengiriman - ${order.order_code}</title>
<style>
@page { size: 105mm 148mm; margin: 0; }
* { margin: 0; padding: 0; box-sizing: border-box; }
body { font-family: 'Segoe UI', Arial, sans-serif; width: 105mm; height: 148mm; padding: 4mm; color: #000; background: #fff; }
.label { width: 100%; height: 100%; border: 2px solid #000; display: flex; flex-direction: column; }
.header { background: #000; color: #fff; text-align: center; padding: 3mm 2mm; font-size: 11pt; font-weight: bold; letter-spacing: 1px; }
.section { padding: 3mm 4mm; border-bottom: 1px dashed #999; }
.section:last-child { border-bottom: none; flex: 1; }
.section-title { font-size: 7pt; font-weight: bold; text-transform: uppercase; color: #666; letter-spacing: 0.5px; margin-bottom: 1mm; }
.name { font-size: 11pt; font-weight: bold; margin-bottom: 0.5mm; }
.detail { font-size: 8.5pt; color: #333; line-height: 1.4; }
.items { font-size: 7.5pt; color: #444; line-height: 1.3; margin-top: 1mm; }
</style></head><body>
<div class="label">
  <div class="header">FI BARBERSHOP SEKIP</div>
  <div class="section">
    <div class="section-title">Pengirim</div>
    <div class="name">${senderName}</div>
    <div class="detail">${senderAddr}</div>
    <div class="detail">Telp: ${senderPhone}</div>
  </div>
  <div class="section">
    <div class="section-title">Penerima</div>
    <div class="name">${order.customer_name}</div>
    <div class="detail">${order.shipping_address || "-"}</div>
    <div class="detail">${[order.subdistrict_name, order.district_name, order.city_name, order.province_name].filter(Boolean).join(", ")}</div>
    <div class="detail">${order.shipping_postal ? "Kode Pos: " + order.shipping_postal : ""}</div>
    <div class="detail">Telp: ${order.customer_phone}</div>
  </div>
  <div class="section">
    <div class="section-title">Isi Paket (${(order.items || []).length} item)</div>
    <div class="items">${(order.items || []).map(i => `${i.name} x${i.qty}`).join(" | ")}</div>
  </div>
</div>
</body></html>`);
    w.document.close();
    setTimeout(() => { w.print(); }, 300);
  };

  if (!order) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#0A0A0A] border-[#262626] text-white max-w-2xl rounded-none max-h-[90vh] overflow-y-auto" data-testid="order-detail-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
            <FileText size={18} className="text-[#E60000]" /> Detail Pesanan
          </DialogTitle>
          <DialogDescription className="text-gray-400 text-sm font-mono">{order.order_code}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Status Row */}
          <div className="flex gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-xs">Pembayaran:</span>
              <Badge className={payColors[order.payment_status] || ""} data-testid="detail-pay-status">{payLabels[order.payment_status] || order.payment_status}</Badge>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-gray-500 text-xs">Status:</span>
              <Badge className={orderColors[order.order_status] || ""} data-testid="detail-order-status">{orderLabels[order.order_status] || order.order_status}</Badge>
            </div>
          </div>

          <Separator className="bg-[#262626]" />

          {/* Items */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5"><Package size={12} /> Item Pesanan</h3>
            <div className="bg-[#111] border border-[#1F1F1F] divide-y divide-[#1F1F1F]">
              {(order.items || []).map((item, i) => (
                <div key={i} className="flex justify-between items-center px-3 py-2 text-sm">
                  <div>
                    <span className="text-white">{item.name}</span>
                    <span className="text-gray-500 ml-2">x{item.qty}</span>
                  </div>
                  <span className="text-gray-300 font-mono text-xs">{formatPrice(item.price * item.qty)}</span>
                </div>
              ))}
              {order.shipping_cost > 0 && (
                <div className="flex justify-between items-center px-3 py-2 text-sm">
                  <span className="text-gray-400">Ongkos Kirim ({order.shipping_courier})</span>
                  <span className="text-gray-300 font-mono text-xs">{formatPrice(order.shipping_cost)}</span>
                </div>
              )}
              <div className="flex justify-between items-center px-3 py-2.5 bg-[#0D0D0D]">
                <span className="text-white font-bold text-sm">Total</span>
                <span className="text-[#E60000] font-bold font-mono">{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>

          <Separator className="bg-[#262626]" />

          {/* Customer Info */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5"><User size={12} /> Informasi Pelanggan</h3>
            <div className="bg-[#111] border border-[#1F1F1F] px-3 py-2.5 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Nama</span><span className="text-white">{order.customer_name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Telepon</span><span className="text-white">{order.customer_phone}</span></div>
              {order.customer_email && <div className="flex justify-between"><span className="text-gray-500">Email</span><span className="text-white">{order.customer_email}</span></div>}
            </div>
          </div>

          {/* Shipping Address */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5"><MapPin size={12} /> Alamat Pengiriman</h3>
            <div className="bg-[#111] border border-[#1F1F1F] px-3 py-2.5 space-y-1.5 text-sm">
              <p className="text-white">{order.shipping_address}</p>
              <p className="text-gray-400 text-xs">{[order.subdistrict_name, order.district_name, order.city_name, order.province_name].filter(Boolean).join(", ")}</p>
              {order.shipping_postal && <p className="text-gray-500 text-xs">Kode Pos: {order.shipping_postal}</p>}
            </div>
          </div>

          {/* Shipping Data */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5"><Truck size={12} /> Data Pengiriman</h3>
            <div className="bg-[#111] border border-[#1F1F1F] px-3 py-2.5 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Kurir</span><span className="text-white">{(order.shipping_courier || "-").toUpperCase()}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Ongkir</span><span className="text-white">{formatPrice(order.shipping_cost || 0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">No. Resi</span><span className="text-white font-mono">{order.awb_number || "-"}</span></div>
              {order.tracking_courier && <div className="flex justify-between"><span className="text-gray-500">Kurir Resi</span><span className="text-white">{order.tracking_courier.toUpperCase()}</span></div>}
              {order.notes && <div className="flex justify-between"><span className="text-gray-500">Catatan</span><span className="text-white">{order.notes}</span></div>}
            </div>
          </div>

          {/* Tracking */}
          {order.awb_number && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5"><Search size={12} /> Tracking Pengiriman</h3>
              <div className="bg-[#111] border border-[#1F1F1F] px-3 py-2.5 text-sm">
                {trackingLoading ? (
                  <p className="text-gray-500 text-center py-2">Memuat tracking...</p>
                ) : tracking && tracking.status !== false ? (
                  <div className="space-y-2">
                    {tracking.result?.manifest ? (
                      tracking.result.manifest.slice(0, 10).map((m, i) => (
                        <div key={i} className="flex gap-3 text-xs">
                          <span className="text-gray-500 whitespace-nowrap">{m.manifest_date} {m.manifest_time}</span>
                          <span className="text-gray-300">{m.manifest_description}</span>
                        </div>
                      ))
                    ) : (
                      <pre className="text-gray-400 text-xs overflow-auto max-h-40">{JSON.stringify(tracking, null, 2)}</pre>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-2">{tracking?.message || "Data tracking tidak tersedia"}</p>
                )}
              </div>
            </div>
          )}

          {/* Payment Info */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5"><CreditCard size={12} /> Pembayaran</h3>
            <div className="bg-[#111] border border-[#1F1F1F] px-3 py-2.5 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Metode</span><span className="text-white">{order.payment_method || "-"}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Status</span><Badge className={`text-[10px] ${payColors[order.payment_status] || ""}`}>{payLabels[order.payment_status] || order.payment_status}</Badge></div>
              <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span className="text-white font-mono text-xs">{formatPrice(order.subtotal || 0)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Ongkir</span><span className="text-white font-mono text-xs">{formatPrice(order.shipping_cost || 0)}</span></div>
              <div className="flex justify-between font-bold"><span className="text-gray-300">Total Bayar</span><span className="text-[#E60000] font-mono">{formatPrice(order.total)}</span></div>
            </div>
          </div>

          {/* Timestamps */}
          <div>
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5"><Clock size={12} /> Waktu</h3>
            <div className="bg-[#111] border border-[#1F1F1F] px-3 py-2.5 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Dibuat</span><span className="text-white">{formatDate(order.created_at)}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Diperbarui</span><span className="text-white">{formatDate(order.updated_at)}</span></div>
              {order.snap_token_created_at && <div className="flex justify-between"><span className="text-gray-500">Snap Token</span><span className="text-white">{formatDate(order.snap_token_created_at)}</span></div>}
            </div>
          </div>

          <Separator className="bg-[#262626]" />

          {/* Print Shipping Label */}
          <Button onClick={printLabel} className="w-full bg-[#1A1A1A] hover:bg-[#262626] text-white font-bold uppercase rounded-none h-10 border border-[#333]" data-testid="print-label-btn">
            <Printer size={14} className="mr-2" /> Cetak Label Pengiriman (A6)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function OrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [awbDialog, setAwbDialog] = useState(false);
  const [awbOrder, setAwbOrder] = useState(null);
  const [awbForm, setAwbForm] = useState({ awb_number: "", tracking_courier: "jne" });
  const [detailOrder, setDetailOrder] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [shopInfo, setShopInfo] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchOrders = useCallback(async () => {
    try { const { data } = await api.get("/orders"); setOrders(data); } catch {} finally { setLoading(false); }
  }, []);

  const fetchShopInfo = useCallback(async () => {
    try { const { data } = await api.get("/settings/shop-info"); setShopInfo(data); } catch {}
  }, []);

  useEffect(() => { fetchOrders(); fetchShopInfo(); }, [fetchOrders, fetchShopInfo]);

  const updateOrderStatus = async (oid, newStatus) => {
    try {
      await api.put(`/orders/${oid}/status`, { order_status: newStatus });
      toast.success(`Status diperbarui ke ${orderLabels[newStatus]}`);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal memperbarui status");
    }
  };

  const openOrderDetail = (order) => {
    setDetailOrder(order);
    setDetailOpen(true);
  };

  const openAwbDialog = (order) => {
    setAwbOrder(order);
    setAwbForm({ awb_number: order.awb_number || "", tracking_courier: order.tracking_courier || "jne" });
    setAwbDialog(true);
  };

  const saveAwb = async () => {
    if (!awbForm.awb_number.trim()) { toast.error("Masukkan nomor resi"); return; }
    try {
      await api.put(`/orders/${awbOrder.id}/awb`, awbForm);
      toast.success("Resi disimpan & status diperbarui");
      setAwbDialog(false);
      fetchOrders();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan resi");
    }
  };

  const filteredOrders = orders.filter(o => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (o.order_code || "").toLowerCase().includes(q)
      || (o.customer_name || "").toLowerCase().includes(q)
      || (o.customer_phone || "").includes(q)
      || (o.awb_number || "").toLowerCase().includes(q);
  });

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-[#E60000] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 md:p-8" data-testid="orders-page">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Pesanan Online</h1>
          <p className="text-gray-400 text-sm mt-1">Kelola pesanan, status, dan input resi pengiriman</p>
        </div>
        <div className="relative w-full sm:w-72">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <Input
            placeholder="Cari kode, nama, telp, resi..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="pl-9 bg-[#0F0F0F] border-[#262626] text-white h-9 rounded-none text-sm"
            data-testid="search-orders"
          />
        </div>
      </div>

      <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="orders-table">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-[#262626]">
            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Kode</th>
            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Pelanggan</th>
            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Total</th>
            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Pembayaran</th>
            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Status Pesanan</th>
            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Resi</th>
            <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-gray-500">Waktu</th>
          </tr></thead>
          <tbody>
            {filteredOrders.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">{searchTerm ? "Tidak ada pesanan yang cocok" : "Belum ada pesanan"}</td></tr>
            ) : filteredOrders.map(o => {
              const nextStatuses = getNextStatuses(o.order_status);
              const canInputAwb = o.payment_status === "paid" && !o.awb_number;
              const hasAwb = !!o.awb_number;

              return (
                <tr key={o.id} className="border-b border-[#262626]/50 hover:bg-[#1A1A1A] transition-colors" data-testid={`order-row-${o.id}`}>
                  {/* Clickable order code */}
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openOrderDetail(o)}
                      className="text-left group"
                      data-testid={`order-code-${o.id}`}
                    >
                      <p className="font-mono text-xs text-[#E60000] group-hover:text-[#FF3333] group-hover:underline transition-colors flex items-center gap-1">
                        {o.order_code} <Eye size={10} className="opacity-0 group-hover:opacity-100 transition-opacity" />
                      </p>
                      <p className="text-gray-500 text-[10px]">{(o.shipping_courier || "").toUpperCase()}</p>
                    </button>
                  </td>
                  <td className="px-4 py-3"><p className="text-white">{o.customer_name}</p><p className="text-gray-500 text-xs">{o.customer_phone}</p></td>
                  <td className="px-4 py-3 text-green-400 font-semibold text-xs font-mono">{formatPrice(o.total)}</td>

                  {/* Payment status - READ ONLY */}
                  <td className="px-4 py-3" data-testid={`pay-status-${o.id}`}>
                    <Badge className={`${payColors[o.payment_status] || ""} text-[10px] cursor-default`}>
                      {payLabels[o.payment_status] || o.payment_status}
                    </Badge>
                  </td>

                  {/* Order status - forward-only */}
                  <td className="px-4 py-3">
                    {nextStatuses.length > 0 && o.payment_status === "paid" ? (
                      <Select value={o.order_status} onValueChange={v => updateOrderStatus(o.id, v)}>
                        <SelectTrigger className="bg-transparent border-0 p-0 h-auto w-auto" data-testid={`order-status-${o.id}`}>
                          <Badge className={orderColors[o.order_status] || ""}>{orderLabels[o.order_status] || o.order_status}</Badge>
                        </SelectTrigger>
                        <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                          <SelectItem value={o.order_status} className="text-white" disabled>
                            {orderLabels[o.order_status]} (saat ini)
                          </SelectItem>
                          {nextStatuses.map(s => (
                            <SelectItem key={s} value={s} className="text-white">
                              <span className="flex items-center gap-1.5"><ChevronRight size={10} /> {orderLabels[s]}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={`${orderColors[o.order_status] || ""} cursor-default`} data-testid={`order-status-${o.id}`}>
                        {orderLabels[o.order_status] || o.order_status}
                      </Badge>
                    )}
                  </td>

                  {/* AWB - only when paid */}
                  <td className="px-4 py-3">
                    {hasAwb ? (
                      <button onClick={() => openAwbDialog(o)} className="text-left group">
                        <p className="font-mono text-xs text-white group-hover:text-[#E60000] transition-colors">{o.awb_number}</p>
                        <p className="text-[10px] text-gray-500">{(o.tracking_courier || "").toUpperCase()}</p>
                      </button>
                    ) : canInputAwb ? (
                      <Button onClick={() => openAwbDialog(o)} className="bg-[#262626] hover:bg-[#333] text-white rounded-none h-7 text-[10px] px-2" data-testid={`add-awb-${o.id}`}>
                        <Truck size={10} className="mr-1" />Input Resi
                      </Button>
                    ) : (
                      <span className="text-gray-600 text-[10px]">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{formatDate(o.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* AWB Dialog */}
      <Dialog open={awbDialog} onOpenChange={setAwbDialog}>
        <DialogContent className="bg-[#0F0F0F] border-[#262626] text-white max-w-sm rounded-none">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2"><Truck size={18} className="text-[#E60000]" /> Input Resi Pengiriman</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">{awbOrder?.order_code} - {awbOrder?.customer_name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-gray-400 text-xs font-bold uppercase">Kurir</label>
              <Select value={awbForm.tracking_courier} onValueChange={v => setAwbForm({...awbForm, tracking_courier: v})}>
                <SelectTrigger className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none" data-testid="awb-courier-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-[#262626]">
                  {courierOptions.map(c => <SelectItem key={c.value} value={c.value} className="text-white">{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-gray-400 text-xs font-bold uppercase">Nomor Resi (AWB)</label>
              <Input className="mt-1 bg-[#1A1A1A] border-[#262626] text-white h-10 rounded-none font-mono" placeholder="Masukkan nomor resi..." value={awbForm.awb_number} onChange={e => setAwbForm({...awbForm, awb_number: e.target.value})} data-testid="awb-input" />
            </div>
            <Button onClick={saveAwb} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase rounded-none h-10" data-testid="save-awb-btn"><Save size={14} className="mr-2" /> Simpan Resi</Button>
            <p className="text-[10px] text-gray-500">Status pesanan otomatis berubah ke "Dikirim" saat resi disimpan.</p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Order Detail Modal */}
      <OrderDetailModal
        order={detailOrder}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        shopInfo={shopInfo}
      />
    </div>
  );
}
